import { useCallback, useSyncExternalStore } from "react";

/**
 * App-wide "machinery rate unit" preference: ₹ per HOUR or ₹ per unit of OUTPUT.
 *
 * Every machinery cost breakup — the fleet list, the rate editor, the deployment
 * norm matrix, the task 3M card and the machinery reports — reads this, and each
 * screen's toolbar toggle writes it. Flipping it on the fleet list also flips
 * the task drawer, deliberately: the unit someone thinks in is a property of the
 * PERSON, not of the screen. A QS who prices in ₹/Cum wants ₹/Cum everywhere.
 *
 * Same tiny external store as useWbsCodes — nothing else needs to react to it,
 * so Redux would be overkill — and localStorage so it survives a reload.
 *
 * HOUR is the default: it is the existing behaviour and everyone's muscle
 * memory. Nobody should have to opt back IN to what they had yesterday.
 */
export type RateUnit = "HOUR" | "OUTPUT";

const STORAGE_KEY = "pms.machineryRateUnit";

/**
 * The nine Equipment Hourly Use Rate engine heads, in the order the workbook
 * (and every screen that shows them) presents them. Mirrors the backend's
 * EQUIPMENT_COST_HEAD_KEYS in src/utils/machinery-rate.util.ts — if a head is
 * ever added there, add it here too or it renders under its raw key.
 *
 * This lives beside the rate-unit helpers because every consumer of the head
 * list is already importing moneyPerUnit from this module. Three screens still
 * carry private copies (TaskQuickViewCard, MonthlyExecution/MonthDetailPage,
 * MachinerySummaryTable) — migrating them is a follow-up; do not add a fourth.
 */
export const EQUIPMENT_HEAD_ORDER: Array<[string, string]> = [
  ["depreciation", "Depreciation"],
  ["interest", "Interest"],
  ["insurance", "Insurance"],
  ["repair", "Repair"],
  ["misc", "Misc"],
  ["electricity", "Electricity"],
  ["fuel", "Fuel"],
  ["lubricants", "Lubricants"],
  ["crew", "Crew"],
];

/** "depreciation" -> "Depreciation"; an unknown key renders as itself. */
export const headLabelOf = (key: string): string =>
  EQUIPMENT_HEAD_ORDER.find(([k]) => k === key)?.[1] || key;

/** Sort key for engine order; unknown heads sort last rather than first. */
export const headOrderOf = (key: string): number => {
  const index = EQUIPMENT_HEAD_ORDER.findIndex(([k]) => k === key);
  return index === -1 ? 99 : index;
};

const read = (): RateUnit => {
  try {
    return localStorage.getItem(STORAGE_KEY) === "OUTPUT" ? "OUTPUT" : "HOUR";
  } catch {
    // Private mode / storage disabled — fall back to the default.
    return "HOUR";
  }
};

let current = read();
const listeners = new Set<() => void>();

const getSnapshot = () => current;

const subscribe = (onChange: () => void) => {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
};

export const setRateUnit = (next: RateUnit) => {
  if (next === current) return;
  current = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Preference just won't persist; the in-memory value still drives the UI.
  }
  listeners.forEach((notify) => notify());
};

// Keep other tabs of the same app in step.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key !== STORAGE_KEY) return;
    current = read();
    listeners.forEach((notify) => notify());
  });
}

/** `[rateUnit, setRateUnit]`, shared across every machinery screen. */
export const useRateUnit = (): [RateUnit, (next: RateUnit) => void] => [
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot),
  setRateUnit,
];

/**
 * Money formatter for per-unit figures.
 *
 * 2 dp normally, escalating to 4 dp when a NON-ZERO value would otherwise
 * render as "0.00". A cheap head on a high-output machine is genuinely
 * sub-paisa per unit — printing it as 0.00 reads as "this head is free" and
 * breaks the head-sum == total reconciliation the column is judged on.
 */
export const moneyPerUnit = (value: any): string => {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  if (n !== 0 && Math.abs(n) < 0.005) {
    return n.toLocaleString("en-IN", {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    });
  }
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export interface HeadValueHelpers {
  unit: RateUnit;
  isPerUnit: boolean;
  /**
   * The value for the ACTIVE unit. Returns undefined — never 0, never the ₹/hr
   * figure — when the per-unit view has no denominator, so the caller renders an
   * em dash. Falling back to the hourly number under a "/Cum" label would be a
   * silently mislabelled unit, the worst failure mode this feature has.
   */
  pick: (heads: any, perUnit: any, key: string) => any;
  /** Formats for the active unit: rupees for ₹/hr, moneyPerUnit for ₹/Cum. */
  format: (value: any) => string;
  /** "₹/hr" or "₹/Cum" — for column titles, footers and totals rows. */
  suffix: (uomName?: string) => string;
}

/**
 * One hook so each table's `render` stays a one-liner and every machinery
 * screen formats identically.
 *
 * NOTE for callers using useMemo on their column definitions: `unit` MUST be in
 * the dependency array, or the columns are captured on first render and the
 * toggle appears to do nothing.
 */
export const useHeadValue = (
  moneyFormatter: (value: any) => string
): HeadValueHelpers => {
  const [unit] = useRateUnit();
  const isPerUnit = unit === "OUTPUT";

  const pick = useCallback(
    (heads: any, perUnit: any, key: string) =>
      isPerUnit ? perUnit?.[key] : heads?.[key],
    [isPerUnit]
  );

  const format = useCallback(
    (value: any) => (isPerUnit ? moneyPerUnit(value) : moneyFormatter(value)),
    [isPerUnit, moneyFormatter]
  );

  const suffix = useCallback(
    (uomName?: string) => (isPerUnit ? `₹/${uomName || "unit"}` : "₹/hr"),
    [isPerUnit]
  );

  return { unit, isPerUnit, pick, format, suffix };
};
